import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiCheckCircle, FiCalendar } from 'react-icons/fi';

const FactoryMaintenance = () => {
  const schedules = [
    { miles: '5,000', service: 'Oil change, tire rotation, multi-point inspection' },
    { miles: '15,000', service: 'Replace cabin air filter, inspect brakes' },
    { miles: '30,000', service: 'Replace engine air filter, transmission service' },
    { miles: '60,000', service: 'Spark plugs, coolant flush, brake fluid' },
    { miles: '90,000', service: 'Timing belt (if applicable), differential service' },
    { miles: '100,000', service: 'Major service - all fluids, filters, thorough inspection' }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>Factory Scheduled Maintenance Dallas | Keep Your Warranty | Paradise-Performance</title>
        <meta name="description" content="Dallas factory maintenance service. Keep your warranty valid. 30k, 60k, 90k mile services. All makes & models. Digital service records. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/factory-maintenance" />
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-green-600 to-green-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/assets/assets/images/new/euro-repair-img/bmw-tensioner-pulleys.webp"
            alt="Factory scheduled maintenance service"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-6">Factory Scheduled Maintenance Dallas</h1>
          <p className="text-xl mb-8 max-w-3xl">
            Keep your warranty. Prevent breakdowns. Save thousands. 
            Factory maintenance isn't a suggestion—it's what keeps your car from becoming a money pit.
          </p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-green-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100">
            Schedule Service: (817) 382-2316
          </a>
        </div>
      </section>

      {/* The Truth */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Why Most Cars Die Early (Spoiler: Skipped Maintenance)</h2>
          <p className="text-lg text-gray-700 mb-6">
            That Toyota that went 300k miles? Regular maintenance. 
            The BMW that died at 80k? Skipped services. 
            It's not luck—it's following the manual.
          </p>
          <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-600">
            <p className="font-semibold mb-2">Real numbers from our shop:</p>
            <p className="text-gray-700">
              Cars with regular maintenance: Average major repair at 150k miles. 
              Cars without: Major repairs start at 60k miles. 
              That's 90,000 miles of difference for a few hundred bucks a year.
            </p>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/transmission-repair/6transmission-repair.webp"
                alt="Oil change service"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white font-bold text-sm">Oil & Filter Service</p>
              </div>
            </div>
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/brakes/brake-repair-hero.webp"
                alt="Brake system inspection"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white font-bold text-sm">Brake System Inspection</p>
              </div>
            </div>
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/radiator/517374-scaled.webp"
                alt="Cooling system service"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white font-bold text-sm">Cooling System Service</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Schedule */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8 text-center">Factory Maintenance Schedule</h2>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-green-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Mileage</th>
                  <th className="px-6 py-4 text-left">Required Service</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold">{item.miles}</td>
                    <td className="px-6 py-4">{item.service}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Why Smart Owners Never Skip Service</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <FiCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-bold mb-1">Warranty Protection</h3>
                <p className="text-gray-700">Skipping service voids warranties. We document everything.</p>
              </div>
            </div>
            <div className="flex items-start">
              <FiCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-bold mb-1">Higher Resale Value</h3>
                <p className="text-gray-700">Service records add $1000s to trade-in value.</p>
              </div>
            </div>
            <div className="flex items-start">
              <FiCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-bold mb-1">Prevent Big Repairs</h3>
                <p className="text-gray-700">$200 service prevents $2000 repair. Every time.</p>
              </div>
            </div>
            <div className="flex items-start">
              <FiCheckCircle className="text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-bold mb-1">No Surprises</h3>
                <p className="text-gray-700">We catch problems early when they're cheap to fix.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Records */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-8">Digital Service Records</h2>
          <p className="text-lg text-gray-700 mb-6">
            Every service logged digitally. Access your complete history anytime. 
            Perfect for warranty claims or selling your car.
          </p>
          <div className="inline-block bg-white p-8 rounded-lg shadow-lg">
            <FiCalendar className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <p className="text-xl font-bold mb-2">Never Miss Service Again</p>
            <p className="text-gray-600">We remind you when service is due</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Smart Owners Maintain</h2>
          <p className="text-xl mb-8">Expensive owners repair. Which one are you?</p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-green-600 px-10 py-5 rounded-lg font-bold text-lg hover:bg-gray-100">
            <FiPhone className="inline mr-2" /> Book Maintenance: (817) 382-2316
          </a>
          <p className="mt-6"><FiMapPin className="inline mr-2" />305 Co Rd 3470
Paradise, TX 76073</p>
        </div>
      </section>
    </motion.div>
  );
};

export default FactoryMaintenance;
